<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { Id, ObjectComment, ObjectRecord } from '@vp/shared-types';
import { boxBounds, ulid } from '@vp/core';
import { live, ui } from '../editor/bridge';
import { keepCanvasSelection } from '../editor/dismiss';
import { formatDate, t, typeLabel } from '../i18n';
import { moduleTypeLabel } from '../modules';
import Icon from './Icon.vue';
import { followLink, linkLabel } from '../editor/bookmarks';

/**
 * Notes on objects (local: the canvas is the user's own): comments and reactions.
 * A small badge sits at the top right corner of every object that has any; a click opens the card.
 */
const REACTIONS = ['👍', '❤️', '🎉', '🔥', '✅', '❓', '⚠️', '💡'];
const MAX_BADGES = 150;

const doc = () => live.editor?.doc;
const noted = (o: ObjectRecord) => !!(o.comments?.length || o.reactions?.length || o.link || o.component);
const hasNotes = (o: ObjectRecord) => !!(o.comments?.length || o.reactions?.length);

const badges = computed(() => {
  void ui.docRev;
  void ui.camRev;
  const d = doc();
  const cam = live.renderer?.camera;
  if (!d || !cam) return [];
  const out: { id: Id; x: number; y: number; comments: number; open: number; reactions: string[]; link: ObjectRecord['link']; notes: boolean; component: { role: string; name: string } | null }[] = [];
  for (const o of d.allObjects()) {
    if (!noted(o)) continue;
    const b = boxBounds(o);
    const p = cam.worldToScreen(b.x + b.w, b.y);
    if (p.x < -40 || p.y < -40 || p.x > cam.width + 200 || p.y > cam.height + 40) continue;
    out.push({ id: o.id, x: p.x, y: p.y, comments: o.comments?.length ?? 0, open: o.comments?.filter((c) => !c.done).length ?? 0, reactions: o.reactions ?? [], link: o.link, notes: hasNotes(o), component: o.component ? { role: o.component.role, name: o.component.name ?? '' } : null });
    if (out.length >= MAX_BADGES) break;
  }
  return out;
});

const target = computed<ObjectRecord | null>(() => {
  void ui.docRev;
  return ui.notesFor ? (doc()?.get(ui.notesFor) ?? null) : null;
});
// the object went away (deleted, other canvas): the card closes
watch(target, (o) => {
  if (!o && ui.notesFor) ui.notesFor = null;
});

const card = ref<HTMLElement | null>(null);
const W = 300;
const place = computed(() => {
  void ui.camRev;
  const o = target.value;
  const cam = live.renderer?.camera;
  if (!o || !cam) return { left: 0, top: 0 };
  const b = boxBounds(o);
  const tl = cam.worldToScreen(b.x, b.y);
  const br = cam.worldToScreen(b.x + b.w, b.y + b.h);
  const right = br.x + 12;
  const left = right + W > cam.width - 12 ? Math.max(12, tl.x - W - 12) : right;
  return { left: Math.min(left, Math.max(12, cam.width - W - 12)), top: Math.min(Math.max(70, tl.y), Math.max(70, cam.height - 380)) };
});

const ro = computed(() => ui.readOnly);
const draft = ref('');
const input = ref<HTMLTextAreaElement | null>(null);
watch(
  () => ui.notesFor,
  async (id) => {
    draft.value = '';
    if (id) {
      await nextTick();
      if (!ro.value) input.value?.focus();
    }
  },
);

function save(patch: Partial<Pick<ObjectRecord, 'comments' | 'reactions'>>): void {
  const o = target.value;
  if (!o || ro.value) return;
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) clean[k] = Array.isArray(v) && v.length ? v : undefined;
  live.editor?.updateObjects([{ id: o.id, patch: clean as Partial<ObjectRecord> }], 'notes');
}

function addComment(): void {
  const text = draft.value.trim().slice(0, 4000);
  const o = target.value;
  if (!text || !o) return;
  const c: ObjectComment = { id: ulid(), text, at: Date.now() };
  save({ comments: [...(o.comments ?? []), c].slice(-500) });
  draft.value = '';
}
function toggleDone(c: ObjectComment): void {
  const o = target.value;
  if (o) save({ comments: (o.comments ?? []).map((x) => (x.id === c.id ? { ...x, done: !x.done || undefined } : x)) });
}
function removeComment(c: ObjectComment): void {
  const o = target.value;
  if (o) save({ comments: (o.comments ?? []).filter((x) => x.id !== c.id) });
}
function toggleReaction(r: string): void {
  const o = target.value;
  if (!o) return;
  const cur = o.reactions ?? [];
  save({ reactions: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] });
}

function open(id: Id): void {
  live.editor?.select([id]);
  ui.notesFor = ui.notesFor === id ? null : id;
}

function onCommentKey(e: KeyboardEvent): void {
  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    addComment();
  }
}
function onCardKey(e: KeyboardEvent): void {
  e.stopPropagation();
  if (e.key === 'Escape') ui.notesFor = null;
}
/** A press outside the card closes it. Badges and the notes button keep their own toggle. On the canvas the press only closes the card. */
function onOutside(e: PointerEvent): void {
  if (!ui.notesFor) return;
  const t = e.target as Element | null;
  if (!t || card.value?.contains(t)) return;
  if (t.closest('[data-act="notes"], .note-badge')) return;
  ui.notesFor = null;
  keepCanvasSelection(e);
}
window.addEventListener('pointerdown', onOutside, true);
onBeforeUnmount(() => window.removeEventListener('pointerdown', onOutside, true));
const title = computed(() => (target.value ? target.value.name || typeLabel(target.value.type, moduleTypeLabel) : ''));
</script>

<template>
  <div class="object-notes">
    <div v-for="b in badges" v-show="!ui.dragging" :key="b.id" class="badges" :style="{ left: b.x + 'px', top: b.y + 'px' }">
    <span v-if="b.component" class="note-badge comp" :class="b.component.role" :data-component="b.id" :title="(b.component.role === 'master' ? t('components.master') : t('components.instance')) + (b.component.name ? ': ' + b.component.name : '')"><Icon name="component" :size="12" /><span v-if="b.component.role === 'master'" class="cname">{{ b.component.name }}</span></span>
    <button v-if="b.link" class="note-badge link" :data-link="b.id" :title="t('links.follow') + ': ' + linkLabel(b.link)" :aria-label="t('links.follow') + ': ' + linkLabel(b.link)" @pointerdown.stop @click="followLink(b.link)"><Icon name="hyperlink" :size="13" /></button>
    <button
      v-if="b.notes"
      class="note-badge"
      :class="{ on: ui.notesFor === b.id }"
      :data-notes="b.id"
      :title="t('notes.open')"
      @pointerdown.stop
      @click="open(b.id)"
    >
      <span v-for="r in b.reactions.slice(0, 3)" :key="r" class="emoji">{{ r }}</span>
      <span v-if="b.comments" class="count" :class="{ done: !b.open }"><Icon name="comment" :size="12" />{{ b.open || b.comments }}</span>
    </button>
    </div>

    <div v-if="target" ref="card" class="float card notes-card" :style="{ left: place.left + 'px', top: place.top + 'px', width: W + 'px' }" role="dialog" :aria-label="t('notes.title')" data-vp-dismiss @pointerdown.stop @keydown="onCardKey">
      <header>
        <strong class="grow">{{ title }}</strong>
        <button class="icon-btn" :aria-label="t('common.close')" @click="ui.notesFor = null"><Icon name="close" :size="14" /></button>
      </header>

      <div class="reactions" role="group" :aria-label="t('notes.reactions')">
        <button v-for="r in REACTIONS" :key="r" class="react" :class="{ on: target.reactions?.includes(r) }" :disabled="ro" :aria-pressed="!!target.reactions?.includes(r)" :data-reaction="r" @click="toggleReaction(r)"><span>{{ r }}</span></button>
      </div>

      <ul v-if="target.comments?.length" class="comments">
        <li v-for="c in target.comments" :key="c.id" :class="{ done: c.done }">
          <button class="check" :class="{ on: c.done }" :disabled="ro" :aria-pressed="!!c.done" :title="t('notes.done')" @click="toggleDone(c)"><Icon v-if="c.done" name="check" :size="11" /></button>
          <p>{{ c.text }}</p>
          <button v-if="!ro" class="x" :aria-label="t('cmd.delete')" @click="removeComment(c)"><Icon name="close" :size="12" /></button>
          <time>{{ formatDate(c.at) }}</time>
        </li>
      </ul>
      <p v-else class="muted empty">{{ t('notes.empty') }}</p>

      <div v-if="!ro" class="add">
        <textarea ref="input" v-model="draft" rows="1" maxlength="4000" :placeholder="t('notes.placeholder')" data-field="comment" @keydown="onCommentKey"></textarea>
        <button class="pill primary" :disabled="!draft.trim()" data-act="add-comment" @click="addComment">{{ t('notes.add') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.object-notes {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 6;
}
.badges {
  position: absolute;
  display: flex;
  gap: 4px;
  transform: translate(-50%, -100%) translateY(-4px);
}
.note-badge {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 3px;
  max-width: 260px;
  height: 24px;
  padding: 0 7px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel);
  box-shadow: var(--shadow-sm, 0 1px 3px rgb(0 0 0 / 0.12));
  color: var(--text);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.note-badge.comp {
  pointer-events: none;
  color: #9333ea;
  padding: 0 6px;
  gap: 4px;
}
.note-badge.comp.master {
  border-color: color-mix(in srgb, #9333ea 45%, transparent);
}
.cname {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}
.note-badge.link {
  color: var(--accent);
  padding: 0 6px;
}
.note-badge.on,
.note-badge:hover {
  border-color: var(--accent);
}
.emoji {
  font-size: 13px;
  line-height: 1;
}
.count {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 2px;
  color: var(--accent);
  font-weight: 600;
}
.count.done {
  color: var(--muted);
}
.notes-card {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: min(440px, calc(100% - 90px));
  overflow-y: auto;
  padding: 10px 12px;
  z-index: 25;
}
header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.grow {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
}
.reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.react {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--hover);
  cursor: pointer;
}
.react span {
  display: block;
  font-size: 14px;
  line-height: 1;
}
.react.on {
  box-shadow: inset 0 0 0 1.5px var(--accent);
}
.react:not(:disabled):hover {
  background: color-mix(in srgb, var(--text) 8%, var(--hover));
}
.x {
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
}
.x:hover {
  opacity: 1;
  background: var(--hover);
}
.comments {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.comments li {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) 18px;
  column-gap: 8px;
  row-gap: 2px;
  align-items: center;
}
.comments li.done p {
  color: var(--muted);
  text-decoration: line-through;
}
.check {
  grid-column: 1;
  grid-row: 1;
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid var(--border-strong);
  border-radius: 5px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  padding: 0;
}
.check.on {
  background: var(--accent);
  border-color: var(--accent);
}
.comments li > p {
  grid-column: 2;
  grid-row: 1;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 13px;
  line-height: 1.35;
}
.comments li > .x {
  grid-column: 3;
  grid-row: 1;
}
time {
  grid-column: 2;
  grid-row: 2;
  font-size: 11px;
  color: var(--muted);
}
.empty {
  margin: 0;
  font-size: 12px;
  color: var(--text-3);
}
.add {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.add textarea {
  flex: 1;
  width: auto;
  box-sizing: border-box;
  resize: none;
  height: 34px;
  min-height: 34px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  padding: 0 12px;
  background: var(--input);
  color: var(--text);
  font: inherit;
  font-size: 13px;
  line-height: 32px;
}
.add .pill {
  height: 34px;
  flex: none;
}
</style>
